import { getSnapshot } from 'mobx-state-tree'
import { useEffect, useRef, useState } from 'react'
import { useStore } from '../../../models'

const DELAY_TO_SHOW_TYPING_INDICATOR = 3000
const DELAY_TO_SHOW_LAST_MESSAGE = 5000

export const useTypingIndicator = () => {
  const {
    session: { user },
    communication
  } = useStore()
  const timeOutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const previousChannelChats = useRef<object[]>([])
  const channelChats = getSnapshot(communication.channelChats)
  const [chatMessage, setChatMessage] = useState('Chat')
  const [isSomeoneTyping, setIsSomeoneTyping] = useState(false)

  const lastMessage = channelChats[channelChats.length - 1]?.message
  const { isTyping, typingUser } = communication
  const isCurrentUserTyping = typingUser === user?._id

  useEffect(() => {
    if (isCurrentUserTyping) {
      // do not handle typing indicator logic if current user is typing
      return
    }

    const isNewMessage = channelChats.length > previousChannelChats.current.length

    if (isNewMessage) {
      // if new message is sent, remove typing indicator, because message is more important
      if (timeOutRef.current) {
        clearTimeout(timeOutRef.current)
        setIsSomeoneTyping(false)
        communication.setIsTyping(false)
      }
      previousChannelChats.current = channelChats
      return
    }

    if (isTyping) {
      // if someone is typing, show typing indicator
      if (timeOutRef.current) {
        clearTimeout(timeOutRef.current)
      }
      setChatMessage('Someone is typing...')
      setIsSomeoneTyping(true)

      communication.setIsTyping(false)
      const timeout = setTimeout(() => {
        setIsSomeoneTyping(false)
        timeOutRef.current = undefined
        setChatMessage('Chat')
      }, DELAY_TO_SHOW_TYPING_INDICATOR)

      // @ts-expect-error
      timeOutRef.current = timeout
    }
    previousChannelChats.current = channelChats
  }, [isTyping, communication, channelChats, isCurrentUserTyping])

  useEffect(() => {
    if (isSomeoneTyping) {
      return
    }

    if (timeOutRef.current) {
      clearTimeout(timeOutRef.current)
      setChatMessage(lastMessage ? `"${lastMessage}"` : 'Chat')
    }

    const timeout = setTimeout(() => {
      setChatMessage('Chat')
    }, DELAY_TO_SHOW_LAST_MESSAGE)

    // @ts-expect-error
    timeOutRef.current = timeout
  }, [isSomeoneTyping, lastMessage, communication])

  return {
    chatMessage
  }
}
